import React, { useState, useEffect, useRef, useMemo } from "react";
import { callApi, HttpMethod } from "src/services/apiService";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import {
  Grid,
  GridColumn as Column,
  GridCellProps,
  GridFilterChangeEvent,
} from "@progress/kendo-react-grid";
import {
  ExcelExport,
  ExcelExportColumn,
} from "@progress/kendo-react-excel-export";
import ReactDOM from "react-dom";
import styles from "./AccountsReceivable.module.css";
import {
  CompositeFilterDescriptor,
  filterBy,
} from "@progress/kendo-data-query";

interface ARAgingData {
  name: string;
  acctId: string;
  totalBalance: number;
  current: number;
  pastDue: number;
  daysPastDue: number;
  days1to30: number;
  days31to60: number;
  days61to90: number;
  days91to120: number;
  days121Plus: number;
  link: string;
}

const loadingPanelMarkup = (
  <div className="k-loading-mask">
    <span className="k-loading-text">Loading</span>
    <div className="k-loading-image" />
    <div className="k-loading-color" />
  </div>
);

const LoadingPanel = (props: any) => {
  const { gridRef } = props;
  const gridContent =
    gridRef.current && gridRef.current.querySelector(".k-grid-content");
  return gridContent
    ? ReactDOM.createPortal(loadingPanelMarkup, gridContent)
    : loadingPanelMarkup;
};

const initialFilter: CompositeFilterDescriptor = {
  logic: "and",
  filters: [],
};

const AccountsReceivable: React.FC = () => {
  const [arData, setArData] = useState<ARAgingData[]>([]);
  const [endDate, setEndDate] = useState<Date | null>(null);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [error, setError] = useState<string>("");
  const [filter, setFilter] =
    useState<CompositeFilterDescriptor>(initialFilter);
  const [showOnlyPastDue, setShowOnlyPastDue] = useState<boolean>(true);
  const gridRef = useRef(null);
  const excelExportRef = useRef<ExcelExport>(null);

  const loadData = async (filterEndDate?: Date | null) => {
    setIsLoading(true);
    setError("");
    try {
      let url = "sysadmin/reports/araging/json";

      if (filterEndDate) {
        // Format date as YYYY-MM-DD
        const year = filterEndDate.getFullYear();
        const month = String(filterEndDate.getMonth() + 1).padStart(2, "0");
        const day = String(filterEndDate.getDate()).padStart(2, "0");
        const endDateStr = `${year}-${month}-${day}`;
        url += `?endDate=${endDateStr}`;
      }

      const response = await callApi(url, HttpMethod.GET);
      setArData(response.data || response);
    } catch (err: any) {
      console.error("Error fetching AR Aging data:", err);
      setError(`Error fetching AR Aging data: ${err.message || err}`);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    loadData();
  }, []);

  const handleDateChange = (date: Date | null) => {
    setEndDate(date);
    loadData(date);
  };

  const handleExport = () => {
    if (excelExportRef.current) {
      excelExportRef.current.save(excelData);
    }
  };

  const handleFilterChange = (e: GridFilterChangeEvent) => {
    setFilter(e.filter);
  };

  // Apply filters and past due filtering
  const filteredData = useMemo(() => {
    let data = arData;

    // Apply "Show Only Past Due" filter
    if (showOnlyPastDue) {
      data = data.filter((item) => item.daysPastDue > 0);
    }

    // Apply Kendo Grid filters
    if (filter && filter.filters && filter.filters.length > 0) {
      data = filterBy(data, filter);
    }

    return data;
  }, [arData, filter, showOnlyPastDue]);

  // Prepare data for Excel export with formatted account names
  const excelData = useMemo(() => {
    return filteredData.map((item) => ({
      Account: `${item.name} (${item.acctId})`,
      "Total Balance": item.totalBalance,
      Current: item.current,
      "Days Past Due": item.daysPastDue,
      "1-30 Days": item.days1to30,
      "31-60 Days": item.days31to60,
      "61-90 Days": item.days61to90,
      "91-120 Days": item.days91to120,
      "121+ Days": item.days121Plus,
    }));
  }, [filteredData]);

  // Custom cell for account name with link
  const AccountNameCell: React.FC<GridCellProps> = (props) => {
    const { dataItem } = props;
    const displayName = `${dataItem.name} (${dataItem.acctId})`;
    const link = dataItem.link;

    return (
      <td>
        <a
          href={link}
          target="_blank"
          rel="noopener noreferrer"
          style={{
            color: "#007bff",
            textDecoration: "underline",
            cursor: "pointer",
          }}
        >
          {displayName}
        </a>
      </td>
    );
  };

  // Custom footer cell for currency aggregates
  const CurrencyFooterCell = (props: any) => {
    const { field } = props;
    const total = filteredData.reduce(
      (sum, item) => sum + (item[field as keyof ARAgingData] as number),
      0,
    );
    return (
      <td style={{ fontWeight: "bold", textAlign: "right" }}>
        $
        {total.toLocaleString("en-US", {
          minimumFractionDigits: 2,
          maximumFractionDigits: 2,
        })}
      </td>
    );
  };

  return (
    <div className={styles.container}>
      <div className={styles.controls}>
        <div className={styles.datePickerWrapper}>
          <label htmlFor="endDate" className={styles.label}>
            As of Date:
          </label>
          <DatePicker
            id="endDate"
            selected={endDate}
            onChange={handleDateChange}
            dateFormat="yyyy-MM-dd"
            isClearable
            placeholderText="Select end date (optional)"
            className="form-control"
          />
        </div>
        <div className={styles.checkboxWrapper}>
          <input
            type="checkbox"
            className="form-check-input"
            id="showOnlyPastDue"
            checked={showOnlyPastDue}
            onChange={(e) => setShowOnlyPastDue(e.target.checked)}
          />
          <label className="form-check-label" htmlFor="showOnlyPastDue">
            Show Only Past Due
          </label>
        </div>

        <button
          onClick={handleExport}
          className="btn btn-primary"
          disabled={isLoading || arData.length === 0}
        >
          Export to Excel
        </button>
      </div>

      {error && (
        <div className="alert alert-danger" role="alert">
          {error}
        </div>
      )}

      <ExcelExport ref={excelExportRef} fileName="AR-Aging-Report.xlsx">
        <ExcelExportColumn field="Account" title="Account" />
        <ExcelExportColumn
          field="Total Balance"
          title="Total Balance"
          cellOptions={{ format: "$#,##0.00" }}
        />
        <ExcelExportColumn
          field="Current"
          title="Current"
          cellOptions={{ format: "$#,##0.00" }}
        />
        <ExcelExportColumn field="Days Past Due" title="Days Past Due" />
        <ExcelExportColumn
          field="1-30 Days"
          title="1-30 Days"
          cellOptions={{ format: "$#,##0.00" }}
        />
        <ExcelExportColumn
          field="31-60 Days"
          title="31-60 Days"
          cellOptions={{ format: "$#,##0.00" }}
        />
        <ExcelExportColumn
          field="61-90 Days"
          title="61-90 Days"
          cellOptions={{ format: "$#,##0.00" }}
        />
        <ExcelExportColumn
          field="91-120 Days"
          title="91-120 Days"
          cellOptions={{ format: "$#,##0.00" }}
        />
        <ExcelExportColumn
          field="121+ Days"
          title="121+ Days"
          cellOptions={{ format: "$#,##0.00" }}
        />
      </ExcelExport>
      <div ref={gridRef} style={{ padding: "5px" }}>
        {isLoading ? <LoadingPanel gridRef={gridRef} /> : null}
        <Grid
          className="sysadmin-grid"
          data={filteredData}
          dataItemKey="acctId"
          sortable={true}
          filterable={true}
          resizable={true}
          filter={filter}
          onFilterChange={handleFilterChange}
          style={{ height: "calc(100vh - 250px)", width: "100%" }}
        >
          {/* Note: cell and footerCell are marked deprecated but cells.footer doesn't exist yet in current Kendo version */}
          <Column
            field="name"
            title="Account"
            cells={{ data: AccountNameCell }}
            footerCell={() => <td style={{ fontWeight: "bold" }}>Total:</td>}
            width={250}
          />
          <Column
            field="totalBalance"
            title="Total Balance"
            format="{0:c2}"
            filter="numeric"
            footerCell={(props) => (
              <CurrencyFooterCell {...props} field="totalBalance" />
            )}
          />
          <Column
            field="current"
            title="Current"
            format="{0:c2}"
            filter="numeric"
            footerCell={(props) => (
              <CurrencyFooterCell {...props} field="current" />
            )}
          />
          <Column field="daysPastDue" title="Days Past Due" filter="numeric" />
          <Column
            field="days1to30"
            title="1-30 Days"
            format="{0:c2}"
            filter="numeric"
            footerCell={(props) => (
              <CurrencyFooterCell {...props} field="days1to30" />
            )}
          />
          <Column
            field="days31to60"
            title="31-60 Days"
            format="{0:c2}"
            filter="numeric"
            footerCell={(props) => (
              <CurrencyFooterCell {...props} field="days31to60" />
            )}
          />
          <Column
            field="days61to90"
            title="61-90 Days"
            format="{0:c2}"
            filter="numeric"
            footerCell={(props) => (
              <CurrencyFooterCell {...props} field="days61to90" />
            )}
          />
          <Column
            field="days91to120"
            title="91-120 Days"
            format="{0:c2}"
            filter="numeric"
            footerCell={(props) => (
              <CurrencyFooterCell {...props} field="days91to120" />
            )}
          />
          <Column
            field="days121Plus"
            title="121+ Days"
            format="{0:c2}"
            filter="numeric"
            footerCell={(props) => (
              <CurrencyFooterCell {...props} field="days121Plus" />
            )}
          />
        </Grid>
      </div>
    </div>
  );
};

export default AccountsReceivable;
